/** SUNTRONIX ONE — reusable server-side searchable combobox.
 *  Debounced async fetch (min 2 chars) with a stable selected label so the
 *  chosen record survives even when it is not on the current result page.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

export function AsyncCombobox({
  value,                 // selected value (string) or ""
  selectedLabel,         // label to show for the current value
  onChange,              // (value, item) => void
  fetcher,               // async (query) => [{ value, label, sub }]
  placeholder = "Search…",
  emptyText = "No matches.",
  minChars = 2,
  disabled = false,
  testId = "async-combobox",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const run = useCallback(async (q) => {
    const term = (q || "").trim();
    if (term.length < minChars) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try { setItems(await fetcher(term)); } catch { setItems([]); }
    finally { setLoading(false); }
  }, [fetcher, minChars]);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => run(query), 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [query, open, run]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setQuery(""); setItems([]); } }}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" role="combobox" aria-expanded={open}
          disabled={disabled} data-testid={`${testId}-trigger`}
          className="w-full h-9 justify-between font-normal text-[13px] px-2.5">
          <span className={cn("truncate", !value && "text-slate-400")}>
            {value ? (selectedLabel || value) : placeholder}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]">
        <Command shouldFilter={false}>
          <CommandInput
            value={query} onValueChange={setQuery}
            placeholder={`Type ${minChars}+ characters…`}
            data-testid={`${testId}-input`} />
          <CommandList>
            {loading && (
              <div className="flex items-center gap-2 px-3 py-4 text-[12px] text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
              </div>
            )}
            {!loading && query.trim().length < minChars && (
              <div className="flex items-center gap-2 px-3 py-4 text-[12px] text-slate-400">
                <Search className="h-3.5 w-3.5" /> Type at least {minChars} characters
              </div>
            )}
            {!loading && query.trim().length >= minChars && items.length === 0 && (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            {!loading && items.length > 0 && (
              <CommandGroup>
                {items.map((it) => (
                  <CommandItem
                    key={it.value} value={it.value}
                    onSelect={() => { onChange(it.value, it); setOpen(false); setQuery(""); }}
                    data-testid={`${testId}-option-${it.value}`}
                    className="cursor-pointer">
                    <Check className={cn("h-3.5 w-3.5", value === it.value ? "opacity-100" : "opacity-0")} />
                    <div className="min-w-0">
                      <div className="truncate text-[13px] text-slate-800">{it.label}</div>
                      {it.sub && <div className="truncate text-[11px] text-slate-400">{it.sub}</div>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
